'use client';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompareType } from '@/hotdesk-ui/enum/compare';

/**
 * Explore page compare mode: whether it is on, and the spaces picked so far
 * in the order they were picked (the first one picked opens in the first
 * column). Kept in Redux so picks survive scrolling, "Show more", filter
 * changes and Grid ⇄ Split. Not persisted — ending compare mode clears it.
 */
export interface ComparePick {
    id: number;
    name: string;
}

interface CompareSelectionState {
    active: boolean;
    /** Type of what is picked. A comparison never mixes workspaces and listings. */
    type: CompareType | null;
    picks: ComparePick[];
}

const initialState: CompareSelectionState = {
    active: false,
    type: null,
    picks: []
};

const compareSelectionSlice = createSlice({
    name: 'compareSelection',
    initialState,
    reducers: {
        startCompareMode: (state, action: PayloadAction<CompareType>) => {
            state.active = true;
            state.type = action.payload;
            state.picks = [];
        },
        exitCompareMode: () => initialState,
        /** Picks a space, or unpicks it when it is already picked. */
        togglePick: (
            state,
            action: PayloadAction<{ pick: ComparePick; maxItems: number }>
        ) => {
            const { pick, maxItems } = action.payload;
            const index = state.picks.findIndex((p) => p.id === pick.id);
            if (index >= 0) {
                state.picks.splice(index, 1);
            } else if (state.picks.length < maxItems) {
                state.picks.push(pick);
            }
        },
        removePick: (state, action: PayloadAction<number>) => {
            state.picks = state.picks.filter((p) => p.id !== action.payload);
        },
        /** The results switched between workspaces and listings: old picks no longer apply. */
        setCompareType: (state, action: PayloadAction<CompareType>) => {
            if (state.type === action.payload) return;
            state.type = action.payload;
            state.picks = [];
        }
    }
});

export const {
    startCompareMode,
    exitCompareMode,
    togglePick,
    removePick,
    setCompareType
} = compareSelectionSlice.actions;
export default compareSelectionSlice.reducer;
